import React from 'react';
import { MEDIA_STATUS } from 'features/video/stores';
import type { MediaEventAction } from 'features/video/stores';
import EVENT_NAMES from 'shared/constants/events';
import Emitter from 'shared/events/emitter';

/**
 * Syncs event results and video overlays into MediaEventStore.
 * Uses ongoingPayload (GET /live) as the source during the live window.
 * Also clears the store when ongoing transitions to null (event ended).
 */
export function useMediaStoreSync(params: {
  scheduleId: string | number | null | undefined;
  eventStarted: boolean;
  ongoingPayload: Record<string, unknown> | null;
  liveEventId: string | number | null;
  run: { phase?: string; eventId?: string | number; eventResults?: Record<string, unknown>; [key: string]: unknown } | null;
  mediaDispatch: React.Dispatch<MediaEventAction>;
}) {
  const {
    scheduleId,
    eventStarted,
    ongoingPayload,
    liveEventId,
    run,
    mediaDispatch,
  } = params;

  const prevOngoingRef = React.useRef<Record<string, unknown> | null | undefined>(undefined);
  const sid = scheduleId != null ? String(scheduleId) : undefined;

  React.useEffect(() => {
    if (sid != null) {
      mediaDispatch({ type: 'SET_ACTIVE_SCHEDULE', scheduleId: sid });
    }
  }, [sid, mediaDispatch]);

  React.useEffect(() => {
    const prev = prevOngoingRef.current;
    const current = ongoingPayload;
    if (scheduleId != null && prev != null && current == null) {
      mediaDispatch({ type: 'SET_RESULTS', payload: {}, scheduleId: sid });
      mediaDispatch({ type: 'SET_VIDEO_OVERLAYS', payload: {}, scheduleId: sid });
      mediaDispatch({ type: 'SET_STATUS', payload: MEDIA_STATUS.ENDED, scheduleId: sid });
    }
    prevOngoingRef.current = current;
  }, [ongoingPayload, scheduleId, sid, mediaDispatch]);

  React.useEffect(() => {
    if (run?.phase === 'execution' && run.eventResults && Object.keys(run.eventResults).length > 0) {
      mediaDispatch({ type: 'SET_RESULTS', payload: run.eventResults, scheduleId: sid });
      mediaDispatch({ type: 'SET_VIDEO_OVERLAYS', payload: (run.eventResults as any).video_overlays || {}, scheduleId: sid });
      return;
    }
    if (!eventStarted || !ongoingPayload || Object.keys(ongoingPayload).length === 0) return;
    if (liveEventId != null && ongoingPayload?.event_id != null && Number(ongoingPayload.event_id) !== Number(liveEventId)) return;
    mediaDispatch({ type: 'SET_RESULTS', payload: ongoingPayload, scheduleId: sid });
    mediaDispatch({ type: 'SET_VIDEO_OVERLAYS', payload: (ongoingPayload as any).video_overlays || {}, scheduleId: sid });
  }, [eventStarted, ongoingPayload, mediaDispatch, run?.phase, run?.eventId, liveEventId, run?.eventResults, sid]);

  React.useEffect(() => {
    mediaDispatch({ type: 'SET_STATUS', payload: MEDIA_STATUS.CANPLAY, scheduleId: sid });
  }, [mediaDispatch, sid]);

  React.useEffect(() => {
    const handler = () => {
      mediaDispatch({ type: 'SET_STATUS', payload: MEDIA_STATUS.PLAYING, scheduleId: sid });
    };
    Emitter.on(EVENT_NAMES.MEDIA.PLAY_PROCEED, handler);
    return () => {
      Emitter.off(EVENT_NAMES.MEDIA.PLAY_PROCEED, handler);
    };
  }, [mediaDispatch, sid]);
}
